import { ensureBaseURL } from '@philips-internal/oswald-common-utils';
import { ConfigurationService, SessionStorageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { deprecationWarningMessage, isFunction } from '@philips-internal/oswald-ui-utils';
import { getModule, getModuleRoute } from '../../util/module.js';
var BrowsingContextMessageType;
(function (BrowsingContextMessageType) {
    BrowsingContextMessageType["CLOSE_REFUSED"] = "browsing-context-close-refused";
    BrowsingContextMessageType["CLOSE_REQUEST"] = "browsing-context-close-request";
    BrowsingContextMessageType["CLOSING"] = "browsing-context-closing";
    BrowsingContextMessageType["READY"] = "browsing-context-ready";
})(BrowsingContextMessageType || (BrowsingContextMessageType = {}));
const BROWSING_CONTEXT_ID_KEY = 'context';
function getBrowsingContextURL(applicationModule) {
    const currentOrigin = new URL(location.href).origin;
    const base = ConfigurationService.getBaseURL();
    const path = ensureBaseURL(getModuleRoute(applicationModule), base);
    return new URL(path, currentOrigin);
}
function createRequestCloseOptions(args) {
    if (typeof args === 'string') {
        deprecationWarningMessage({
            deadlineVersion: '7.0.0',
            replaced: 'requestClose(contextId, dialogDescription)',
            replacement: 'requestClose(contextId, options)',
            scope: 'BrowsingContextService',
            startingVersion: '6.30.3',
        });
    }
    return {
        askForConfirmation: typeof args === 'string' ? true : (args?.askForConfirmation ?? true),
        dialogDescription: typeof args === 'string' ? args : args?.dialogDescription,
    };
}
function createBrowsingContextService() {
    function getBroadcastChannel(id) {
        const name = `oswald:broadcast-channel:${id}`;
        return new BroadcastChannel(name);
    }
    const listenMessage = (contextId, messageType, callback) => {
        const channel = getBroadcastChannel(contextId);
        const abortController = new AbortController();
        const onMessage = (event) => {
            const payload = event.data;
            if (payload.type === messageType) {
                callback(payload.data);
            }
        };
        channel.addEventListener('message', onMessage, { signal: abortController.signal });
        return () => {
            abortController.abort();
            channel.close();
        };
    };
    const open = (options) => {
        const { onClose, onReady } = options;
        const url = getBrowsingContextURL(getModule(options.moduleId));
        url.searchParams.append(BROWSING_CONTEXT_ID_KEY, options.contextId);
        window.open(url, '_blank', 'popup=true,location=no');
        if (isFunction(onReady)) {
            const unlisten = listenMessage(options.contextId, BrowsingContextMessageType.READY, () => {
                onReady();
                unlisten();
            });
        }
        if (isFunction(onClose)) {
            const unlisten = listenMessage(options.contextId, BrowsingContextMessageType.CLOSING, () => {
                onClose();
                unlisten();
            });
        }
        return {
            listenMessage: (messageType, callback) => listenMessage(options.contextId, messageType, callback),
            requestClose: (args) => requestClose(options.contextId, args),
            sendMessage: (message) => sendMessage(options.contextId, message),
        };
    };
    const requestClose = (contextId, args) => {
        const channel = getBroadcastChannel(contextId);
        const abortController = new AbortController();
        const cleanup = () => {
            abortController.abort();
            channel.close();
        };
        return new Promise((resolve) => {
            const onMessage = (event) => {
                const payload = event.data;
                if (payload.type === BrowsingContextMessageType.CLOSE_REFUSED) {
                    return resolve(false);
                }
                if (payload.type === BrowsingContextMessageType.CLOSING) {
                    return resolve(true);
                }
            };
            channel.addEventListener('message', onMessage, { signal: abortController.signal });
            const options = createRequestCloseOptions(args);
            const message = {
                data: options,
                type: BrowsingContextMessageType.CLOSE_REQUEST,
            };
            channel.postMessage(message);
        }).finally(cleanup);
    };
    const sendMessage = (contextId, message) => {
        const channel = getBroadcastChannel(contextId);
        channel.postMessage(message);
        channel.close();
    };
    const getCurrentContextId = () => {
        return SessionStorageService.getItem(BROWSING_CONTEXT_ID_KEY);
    };
    return {
        getCurrentContextId,
        listenMessage,
        open,
        requestClose,
        sendMessage,
    };
}
/**
 * Service to create and communicate with different browsing contexts.
 */
const BrowsingContextService = createBrowsingContextService();
export { BROWSING_CONTEXT_ID_KEY, BrowsingContextMessageType, BrowsingContextService, };
//# sourceMappingURL=browsing-context-service.js.map